import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ReplayIcon from '@mui/icons-material/Replay'
import { useTranslation } from 'react-i18next'
import {
  YardPhoto,
  getYardPhotos,
  uploadYardPhoto,
  reanalyzeYardPhoto,
  deleteYardPhoto,
  resizeImageFile,
} from '../../services/yardPhotos'

const ButtonsRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${props => props.theme.spacing.md};
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${props => props.theme.spacing.md};
`

const PhotoCard = styled.div`
  border-radius: ${props => props.theme.borderRadius.large};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundSecondary};
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  }
`

const PhotoImg = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
  display: block;
`

const PhotoCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.sm};
`

const JournalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.lg};
`

const JournalEntry = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.large};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundSecondary};
  cursor: pointer;
`

const JournalThumb = styled.img`
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: ${props => props.theme.borderRadius.medium};
  flex-shrink: 0;
`

const DialogImg = styled.img`
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  border-radius: ${props => props.theme.borderRadius.medium};
  background: #111;
`

const POLL_INTERVAL_MS = 5000
const MAX_POLLS = 24 // ~2 minutes

interface Props {
  yardId: string
}

/**
 * Site photo gallery + AI journal for a yard: upload photos, view AI conclusions
 * per photo, and a journal of what changed between consecutive photos.
 */
function YardPhotoJournal({ yardId }: Props): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const [photos, setPhotos] = useState<YardPhoto[]>([])
  const [uploading, setUploading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pollCount = useRef(0)

  const selected = photos.find(p => p.id === selectedId) || null

  const fmtDate = useCallback((dateStr?: string): string => {
    if (!dateStr) return ''
    try {
      return new Date(dateStr).toLocaleDateString(i18n.language)
    } catch {
      return dateStr
    }
  }, [i18n.language])

  const loadPhotos = useCallback(async (): Promise<void> => {
    try {
      const res = await getYardPhotos(yardId)
      setPhotos(res.data || [])
    } catch (error) {
      console.error('Error loading yard photos:', error)
    }
  }, [yardId])

  useEffect(() => {
    loadPhotos()
  }, [loadPhotos])

  // Poll while an analysis is pending (bounded)
  useEffect(() => {
    const hasPending = photos.some(p => p.analysisStatus === 'pending')
    if (!hasPending || pollCount.current >= MAX_POLLS) return
    const timer = setTimeout(() => {
      pollCount.current += 1
      loadPhotos()
    }, POLL_INTERVAL_MS)
    return () => clearTimeout(timer)
  }, [photos, loadPhotos])

  async function handleFileSelected(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    try {
      setUploading(true)
      const { blob, fileName } = await resizeImageFile(file)
      const res = await uploadYardPhoto(yardId, blob, fileName, (i18n.language || 'en').split('-')[0])
      pollCount.current = 0
      setPhotos(prev => [res.data, ...prev])
    } catch (error) {
      console.error('Error uploading yard photo:', error)
      window.alert(t('yardPhotos.uploadFailed'))
    } finally {
      setUploading(false)
    }
  }

  async function handleRetry(photo: YardPhoto): Promise<void> {
    try {
      await reanalyzeYardPhoto(yardId, photo.id)
      pollCount.current = 0
      loadPhotos()
    } catch (error) {
      console.error('Error re-analyzing yard photo:', error)
    }
  }

  async function handleDelete(photo: YardPhoto): Promise<void> {
    const ok = window.confirm(
      t('yardPhotos.deleteConfirm')
    )
    if (!ok) return
    try {
      await deleteYardPhoto(yardId, photo.id)
      setSelectedId(null)
      loadPhotos()
    } catch (error) {
      console.error('Error deleting yard photo:', error)
    }
  }

  function statusChip(photo: YardPhoto): React.JSX.Element | null {
    if (photo.analysisStatus === 'pending') {
      return (
        <Chip
          size="small"
          color="warning"
          icon={<CircularProgress size={12} color="inherit" />}
          label={t('yardPhotos.analyzing')}
        />
      )
    }
    if (photo.analysisStatus === 'failed') {
      return (
        <Chip
          size="small"
          color="error"
          label={t('yardPhotos.analysisFailed')}
        />
      )
    }
    return null
  }

  return (
    <div>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />

      <ButtonsRow>
        <Button
          variant="contained"
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <PhotoCameraIcon />}
          disabled={uploading}
          onClick={() => cameraInputRef.current?.click()}
        >
          {t('yardPhotos.takePhoto')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<AddPhotoAlternateIcon />}
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {t('yardPhotos.addPhoto')}
        </Button>
      </ButtonsRow>

      {photos.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {t('yardPhotos.noPhotos')}
        </Typography>
      ) : (
        <>
          <Grid>
            {photos.map(photo => (
              <PhotoCard key={photo.id} onClick={() => setSelectedId(photo.id)}>
                <PhotoImg src={photo.imageUrl} alt={fmtDate(photo.takenAt)} loading="lazy" />
                <PhotoCardFooter>
                  <Typography variant="caption" fontWeight={600}>
                    {fmtDate(photo.takenAt)}
                  </Typography>
                  {statusChip(photo)}
                </PhotoCardFooter>
              </PhotoCard>
            ))}
          </Grid>

          <JournalList>
            <Typography variant="subtitle1" fontWeight={700}>
              {t('yardPhotos.journal')}
            </Typography>
            {photos.map((photo, index) => {
              const isFirstEver = index === photos.length - 1
              return (
                <JournalEntry key={photo.id} onClick={() => setSelectedId(photo.id)}>
                  <JournalThumb src={photo.imageUrl} alt={fmtDate(photo.takenAt)} loading="lazy" />
                  <div>
                    <Typography variant="subtitle2">{fmtDate(photo.takenAt)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {photo.comparison?.previousTakenAt
                        ? `${t('yardPhotos.comparedTo', {
                            date: fmtDate(photo.comparison.previousTakenAt),
                          })} · ${t('yardPhotos.daysBetween', {
                            count: photo.comparison.daysBetween || 0,
                          })}`
                        : isFirstEver
                        ? t('yardPhotos.firstPhoto')
                        : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {photo.analysisStatus === 'pending'
                        ? t('yardPhotos.analyzing')
                        : photo.analysisStatus === 'failed'
                        ? t('yardPhotos.analysisFailed')
                        : photo.comparison?.summary || photo.analysis?.summary || ''}
                    </Typography>
                  </div>
                </JournalEntry>
              )
            })}
          </JournalList>
        </>
      )}

      <Dialog open={!!selected} onClose={() => setSelectedId(null)} fullWidth maxWidth="md">
        {selected && (
          <>
            <DialogTitle>
              {fmtDate(selected.takenAt)}
              {selected.userName ? ` · ${selected.userName}` : ''}
            </DialogTitle>
            <DialogContent>
              <DialogImg src={selected.imageUrl} alt={fmtDate(selected.takenAt)} />

              {selected.analysisStatus === 'pending' && (
                <Typography variant="body2" sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  {t('yardPhotos.analyzing')}
                </Typography>
              )}

              {selected.analysisStatus === 'failed' && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                  {t('yardPhotos.analysisFailed')}
                  {selected.analysisError ? ` — ${selected.analysisError}` : ''}
                </Typography>
              )}

              {selected.analysisStatus === 'done' && selected.analysis && (
                <>
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    {selected.analysis.summary}
                  </Typography>
                  {selected.analysis.hiveCountEstimate != null && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      🐝 {t('yardPhotos.hiveEstimate', {
                        count: selected.analysis.hiveCountEstimate,
                      })}
                    </Typography>
                  )}

                  {(selected.analysis.observations?.length || 0) > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 2 }}>
                        {t('yardPhotos.observations')}
                      </Typography>
                      <ul style={{ marginTop: 4 }}>
                        {selected.analysis.observations!.map((obs, i) => (
                          <li key={i}>
                            <Typography variant="body2">{obs}</Typography>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {(selected.analysis.concerns?.length || 0) > 0 && (
                    <>
                      <Typography variant="subtitle2" color="warning.main" sx={{ mt: 1 }}>
                        ⚠️ {t('yardPhotos.concerns')}
                      </Typography>
                      <ul style={{ marginTop: 4 }}>
                        {selected.analysis.concerns!.map((concern, i) => (
                          <li key={i}>
                            <Typography variant="body2">{concern}</Typography>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {selected.comparison?.summary && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" color="primary">
                        {t('yardPhotos.comparedTo', {
                          date: fmtDate(selected.comparison.previousTakenAt),
                        })}{' '}
                        · {t('yardPhotos.daysBetween', {
                          count: selected.comparison.daysBetween || 0,
                        })}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {selected.comparison.summary}
                      </Typography>
                      {(selected.comparison.changes?.length || 0) > 0 && (
                        <ul style={{ marginTop: 4 }}>
                          {selected.comparison.changes!.map((change, i) => (
                            <li key={i}>
                              <Typography variant="body2">{change}</Typography>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              {selected.analysisStatus === 'failed' && (
                <Button startIcon={<ReplayIcon />} onClick={() => handleRetry(selected)}>
                  {t('yardPhotos.retry')}
                </Button>
              )}
              <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => handleDelete(selected)}>
                {t('yardPhotos.deleteTitle')}
              </Button>
              <Button onClick={() => setSelectedId(null)}>
                {t('yardPhotos.close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  )
}

export default YardPhotoJournal
